import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import { Dispatch, connect } from 'react-redux';
import { IMoveTracker, ILogistics, ILogisticsResponse, IDutyOfficer } from '@nkr/models/index';
import { DisplayType } from '@nkr/enums/index';
import { AppActionCreators, SwapActionCreators, IApplicationState, IMoveTrackerListResponse, IDutyOfficerResponse } from '@nkr/store/index';
import MoveTrackerList from '@nkr/views/swap-management/swaps/move-tracker/move-tracker-list';
import LogisticsView from '@nkr/views/swap-management/swaps/swap-list/logistics/view';
import { ToggleButtonGroup, ToggleButton, InputGroup, Alert, Popover, OverlayTrigger, Button } from 'react-bootstrap';
import MaskedInput from 'react-maskedinput';

interface IOwnProps {
    centerId: number
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getSwapsInProcess: (isOps: boolean, centerId: number) => Promise<IMoveTrackerListResponse>,
    getSwapsForMoveTracker: (isOps: boolean, centerId: number) => Promise<IMoveTrackerListResponse>,
    getLogisticsData: (donorId: number, recipientId: number) => Promise<ILogisticsResponse>,
    getCurrentDutyOfficer: () => Promise<IDutyOfficerResponse>
}

type TProps =
    IOwnProps
    & IStateProps
    & IDispatchProps;

interface IState {
    list: IMoveTracker[],
    autoRefresh: number,
    loading: boolean,
    showSwaps: string,
    displayTimeZone: string,
    startDate: string,
    endDate: string,
    logistics: {
        show: boolean,
        data: ILogistics | null
    },
    dutyofficer: IDutyOfficer,
    setFade: string
}

const initialState: IState = {
    list: [],
    autoRefresh: 0,
    loading: true,
    showSwaps: 'ALL',
    displayTimeZone: 'DONORCENTER',
    startDate: moment().day(0).format('MM/DD/YYYY'),
    endDate: moment().day(0).add(2, 'week').format('MM/DD/YYYY'),
    logistics: {
        show: true,
        data: null
    },
    dutyofficer: null,
    setFade: ''
};

class CenterMoveTracker extends React.Component<TProps, IState> {
    readonly state: IState = initialState;

    dutyOfficerTimer: any;

    componentDidMount = () => {
        this.getData();
    }

    handleSwapViewChange = (view: string) => {
        this.setState({
            list: [],
            showSwaps: view,
            loading: true
        }, () => {
            return this.getData().then(() => {
                this.setState({
                    loading: false
                });
            });
        });
    }

    handleTimeZoneChange = (tz: string) => {
        this.setState({
            displayTimeZone: tz
        });
    }

    handleCloseLogistics = () => {
        this.setState({
            logistics: {
                show: false,
                data: null
            }
        });
    }

    handleViewLogistics = (item: IMoveTracker) => {
        this.props.showLoading('Fetching logistics information, one moment please...').then(() => {
            this.props.getLogisticsData(item.donorId, item.recipientId).then((response: ILogisticsResponse) => {
                if (response.data.success) {
                    this.setState({
                        logistics: {
                            show: true,
                            data: response.data.results
                        }
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleStartDateChange = (e: React.FormEvent<any>) => {
        this.setState({
            startDate: e.currentTarget.value
        });
    }

    handleEndDateChange = (e: React.FormEvent<any>) => {
        this.setState({
            endDate: e.currentTarget.value
        });
    }

    getAllSwaps = () => {
        return this.props.getSwapsForMoveTracker(false, this.props.centerId).then(response => {
            this.setState({
                list: response.data.results,
                loading: false
            }, () => {
                return Promise.resolve();
            });
        });
    }

    getTodaySwaps = () => {
        return this.props.getSwapsInProcess(false, this.props.centerId).then(response => {
            this.setState({
                list: response.data.results,
                loading: false
            }, () => {
                return Promise.resolve();
            });
        });
    }

    getData = () => {
        this.dutyOfficerTimer = window.setInterval(this.checkForDutyOfficer, 5000);

        if (this.state.showSwaps == 'ALL') {
            return this.getAllSwaps();
        } else {
            return this.getTodaySwaps();
        }
    }

    checkForDutyOfficer = () => {
        this.props.getCurrentDutyOfficer().then(async response => {
            if (response.data.success) {
                if (this.state.dutyofficer !== null && (this.state.dutyofficer.id !== response.data.result.id)) {
                    this.setState({
                        setFade: 'fade-out'
                    });
                }

                this.setState({
                    dutyofficer: response.data.result,
                    setFade: 'fade-in'
                });
            }

            this.dutyOfficerTimer = window.setInterval(this.checkForDutyOfficer, 60000);
        });
    }

    render = () => {
        const list = _.orderBy(this.state.list.filter((swap: IMoveTracker) => {
            if (this.state.showSwaps == 'ALL') {
                let startDate = moment(this.state.startDate, 'MM/DD/YYYY', true);
                let endDate = moment(this.state.endDate, 'MM/DD/YYYY', true);
                let kidneyPickup = moment(swap.kidneyPickup, 'MM/DD/YY HH:mm', true);

                if (startDate.isValid() && endDate.isValid() && kidneyPickup.isValid()) {
                    return kidneyPickup.isSameOrAfter(startDate, 'day') && kidneyPickup.isSameOrBefore(endDate, 'day');
                } else {
                    return false;
                }
            } else {
                return new Date(swap.kidneyPickup) < new Date(new Date().toLocaleDateString() + ' 23:59:59');
            }
        }), (item: IMoveTracker) => {
            return new Date(item.kidneyPickup);
        }, ['asc']);

        const dutyOfficerOverlay = (
            <Popover id='alertpop' bsStyle='warning' style={{ background: '#fcf8e3', width: '300px' }} >
                <div>{this.state.dutyofficer ? this.state.dutyofficer.name : 'Loading...'}</div>
                <div>{this.state.dutyofficer ? this.state.dutyofficer.phoneNumber : '...'}</div>
                <div>{this.state.dutyofficer ? this.state.dutyofficer.email : '...'}</div><br />
                <div style={{ fontSize: 'small', textAlign: 'left' }}>You can always contact NKR Logistics via <br />email (nkrlogistics@kidneyregistry.org) if <br />
                    you cannot reach &nbsp;<b>{this.state.dutyofficer ? this.state.dutyofficer.name : 'Loading...'}</b>
                </div>
            </Popover>
        );

        return (
            <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 30, marginBottom: 20, marginTop: 0 }}>
                    <div><h4 style={{ margin: 0 }}>Move Tracker</h4></div>

                    <Alert bsStyle='info' style={{ margin: 0, paddingTop: 3, paddingBottom: 3 }}>
                        For any questions regarding an actively moving kidney please contact the current officer on duty&nbsp;
                        <OverlayTrigger trigger='click' overlay={dutyOfficerOverlay} rootClose defaultOverlayShown={false} ><Button bsSize='xs' bsStyle='success' onClick={(e: any) => e.stopPropagation()}><div className={this.state.setFade}>{this.state.dutyofficer ? this.state.dutyofficer.name : 'Loading Officer...'}</div></Button></OverlayTrigger><br />
                    </Alert>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {this.state.showSwaps == 'ALL' && (
                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                <div style={{ marginRight: 5, fontSize: 12 }}>Pickup Date Range:</div>
                                <div>
                                    <InputGroup bsSize='sm'>
                                        <MaskedInput
                                            className='form-control input-sm'
                                            style={{ borderRight: '0 none' }}
                                            mask='11/11/1111'
                                            name='startDate'
                                            value={this.state.startDate}
                                            onChange={this.handleStartDateChange}
                                        />
                                        <InputGroup.Addon>&nbsp;to&nbsp;</InputGroup.Addon>
                                        <MaskedInput
                                            className='form-control input-sm'
                                            style={{ borderLeft: '0 none' }}
                                            mask='11/11/1111'
                                            name='endDate'
                                            value={this.state.endDate}
                                            onChange={this.handleEndDateChange}
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 5 }}>
                            <div style={{ marginRight: 5 }}>Swaps:</div>
                            <div style={{ marginRight: 10 }}>
                                <ToggleButtonGroup type='radio' name='swaps' value={this.state.showSwaps}>
                                    <ToggleButton type='radio' value={'ALL'} className={this.state.showSwaps == 'ALL' ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => this.handleSwapViewChange('ALL')}>All</ToggleButton>
                                    <ToggleButton type='radio' value={'TODAY'} className={this.state.showSwaps == 'TODAY' ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => this.handleSwapViewChange('TODAY')}>Today's</ToggleButton>
                                </ToggleButtonGroup>
                            </div>

                            <div style={{ marginRight: 5 }}>Time&nbsp;zone:</div>
                            <div>
                                <ToggleButtonGroup type='radio' name='swaps' value={this.state.displayTimeZone}>
                                    <ToggleButton type='radio' value={'DONORCENTER'} className={this.state.displayTimeZone == 'DONORCENTER' ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => this.handleTimeZoneChange('DONORCENTER')}>Donor Center</ToggleButton>
                                    <ToggleButton type='radio' value={'YOUR'} className={this.state.displayTimeZone == 'YOUR' ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => this.handleTimeZoneChange('YOUR')}>Local</ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                        </div>
                    </div>
                </div>

                <MoveTrackerList
                    list={list}
                    displayType={DisplayType.All}
                    displayTimeZone={this.state.displayTimeZone}
                    backgroundColor={'white'}
                    textColor={'#123'}
                    borderColor={'#486da8'}
                    headerBackgroundColor={'#eee'}
                    enableNotes={false}
                    isCourier={false}
                    onSwitchPlan={() => null}
                    onViewLogistics={this.handleViewLogistics}
                    onSaveNotes={() => null}
                    onAcknowledgeAlert={() => null}
                />

                {list.length == 0 && (
                    <div className='text-center'>
                        There are no swaps for today...
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {list.length > 0 && (
                        <div>
                            {list.length} swaps ...
                        </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                    </div>
                </div>

                <LogisticsView
                    show={this.state.logistics.show}
                    data={this.state.logistics.data}
                    onClose={this.handleCloseLogistics}
                />
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return ({
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getSwapsInProcess: (isOps, centerId) => {
            return dispatch(SwapActionCreators.getSwapsInProcess(isOps, centerId));
        },
        getSwapsForMoveTracker: (isOps, centerId) => {
            return dispatch(SwapActionCreators.getSwapsForMoveTracker(isOps, centerId));
        },
        getLogisticsData: (donorId: number, recipientId: number) => {
            return dispatch(SwapActionCreators.getLogisticsDataByDonorRecipient(donorId, recipientId));
        },
        getCurrentDutyOfficer: () => {
            return dispatch(SwapActionCreators.getCurrentDutyOfficer());
        }
    });
}

export default connect<{}, IDispatchProps, IOwnProps>(
    null,
    mapDispatchToProps)(CenterMoveTracker);

